import React, { createContext, useContext, ReactNode } from 'react';
import { usePWAInstall } from '../hooks/usePWAInstall';

type BrowserType = 'chrome' | 'safari' | 'firefox' | 'edge' | 'samsung' | 'opera' | 'other';
type OSType = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'other';

interface PWAInstallContextValue {
  // Core state
  isInstallable: boolean;
  isStandalone: boolean;
  canPrompt: boolean;

  // Browser/OS detection
  browser: BrowserType;
  os: OSType;

  // Actions
  promptInstall: () => Promise<boolean>;
  dismissPrompt: () => void;

  // UI state
  showInstructions: boolean;
  setShowInstructions: (show: boolean) => void;
}

const PWAInstallContext = createContext<PWAInstallContextValue | undefined>(undefined);

interface PWAInstallProviderProps {
  children: ReactNode;
}

/**
 * PWA Install Provider
 *
 * Provides PWA installation state and methods to all child components.
 * This ensures all components share the same PWA install state.
 */
export const PWAInstallProvider: React.FC<PWAInstallProviderProps> = ({ children }) => {
  const pwaInstallState = usePWAInstall();

  return (
    <PWAInstallContext.Provider value={pwaInstallState}>
      {children}
    </PWAInstallContext.Provider>
  );
};

/**
 * Hook to access PWA install context
 *
 * Must be used within a PWAInstallProvider.
 */
export const usePWAInstallContext = (): PWAInstallContextValue => {
  const context = useContext(PWAInstallContext);

  if (context === undefined) {
    throw new Error('usePWAInstallContext must be used within a PWAInstallProvider');
  }

  return context;
};
